import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PageHeader from '../../components/common/PageHeader';
import SourceCombobox from '../../components/common/SourceCombobox';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import { USER_ROUTES } from '../../constants/routeConstants';
import { MT5AccountUser } from '../../utils/types';
import { mt5API } from '../../services/api';
import {
  getCommentValidationMessage,
  MAX_COMMENT_LENGTH,
  MAX_TRANSACTION_AMOUNT,
  MIN_COMMENT_LENGTH,
  MIN_TRANSACTION_AMOUNT,
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';

const Transfer = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<MT5AccountUser[]>([]);
  const [fromAccount, setFromAccount] = useState<string>('');
  const [toAccount, setToAccount] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    fromAccount?: string;
    toAccount?: string;
    amount?: string;
    comment?: string;
  }>({});

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await mt5API.getUsers();
      const data = response?.data || response;
      if (Array.isArray(data) && data.length > 0) {
        const mapped = data.map((acc: any) => ({
          ...acc,
          Login: acc.mt5Login ?? acc.Login ?? acc.id,
          Name: acc.name ?? acc.Name ?? 'Account',
          Group: acc.group ?? acc.Group ?? '-',
          Balance: Number(acc.balance ?? acc.Balance ?? 0),
          Leverage: acc.leverage ?? acc.Leverage,
          Credit: Number(acc.credit ?? acc.Credit ?? 0),
        }));
        setAccounts(mapped);
      }
    } catch (error) {
      console.error('Error fetching accounts for transfer:', error);
      toast.error('Failed to load trading accounts.');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fromAccountsList = accounts.filter(
    acc => String(acc.Login || (acc as any).mt5Login) !== String(toAccount)
  );

  const toAccountsList = accounts.filter(
    acc => String(acc.Login || (acc as any).mt5Login) !== String(fromAccount)
  );

  const fromAccountObj = accounts.find(
    acc => String(acc.Login || (acc as any).mt5Login) === String(fromAccount)
  );

  const toAccountObj = accounts.find(
    acc => String(acc.Login || (acc as any).mt5Login) === String(toAccount)
  );

  const validate = () => {
    const newErrors: {
      fromAccount?: string;
      toAccount?: string;
      amount?: string;
      comment?: string;
    } = {};

    if (!fromAccount) {
      newErrors.fromAccount = 'Please select a source trading account.';
    }

    if (!toAccount) {
      newErrors.toAccount = 'Please select a destination trading account.';
    } else if (fromAccount && fromAccount === toAccount) {
      newErrors.toAccount =
        'Source and destination accounts cannot be the same.';
    }

    const numericAmount = Number(amount);

    if (!amount || isNaN(numericAmount)) {
      newErrors.amount = `Please enter a valid transfer amount between $${MIN_TRANSACTION_AMOUNT} and $${MAX_TRANSACTION_AMOUNT}.`;
    } else if (numericAmount < MIN_TRANSACTION_AMOUNT) {
      newErrors.amount = `Transfer amount must be at least $${MIN_TRANSACTION_AMOUNT}.`;
    } else if (numericAmount > MAX_TRANSACTION_AMOUNT) {
      newErrors.amount = `Transfer amount cannot exceed $${MAX_TRANSACTION_AMOUNT}.`;
    } else if (
      fromAccountObj &&
      numericAmount > (fromAccountObj.Balance || 0)
    ) {
      newErrors.amount = `Transfer amount cannot exceed available balance ($${(fromAccountObj.Balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}).`;
    }

    const commentError = getCommentValidationMessage(comment);
    if (commentError) {
      newErrors.comment = commentError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        fromLogin: isNaN(Number(fromAccount))
          ? fromAccount
          : Number(fromAccount),
        toLogin: isNaN(Number(toAccount)) ? toAccount : Number(toAccount),
        amount: Number(amount),
        comment: comment.trim(),
      };

      const response = await mt5API.transfer(payload);
      if (response?.success !== false) {
        toast.success(response?.message || 'Internal transfer successful!');
        setFromAccount('');
        setToAccount('');
        setAmount('');
        setComment('');
        setErrors({});
        fetchAccounts();
      } else {
        toast.error(response?.message || 'Transfer failed.');
      }
    } catch (error: any) {
      console.error('Error executing transfer:', error);
      toast.error(
        error?.response?.data?.message || 'Error processing transfer request.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LoaderOverlay
      isLoading={isSubmitting}
      loadingText="Processing Transfer..."
    >
      <div className="space-y-6">
        <div className="sticky top-0 z-20 bg-background pt-4">
          <PageHeader
            title="Internal Transfer"
            description="Move funds instantly between your trading accounts and wallets."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Transfer Details</CardTitle>
            </CardHeader>
            <CardContent>
              <LoaderOverlay
                isLoading={loadingAccounts}
                loadingText="Loading trading accounts..."
              >
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fromAccount">
                        From Account <span className="text-red-500">*</span>
                      </Label>
                      <SourceCombobox
                        id="fromAccount"
                        items={fromAccountsList.map(
                          acc =>
                            `${acc.Login || (acc as any).mt5Login} (${acc.Name || (acc as any).name || 'Account'})`
                        )}
                        placeholder="Select from account"
                        value={
                          fromAccountObj
                            ? `${fromAccountObj.Login || (fromAccountObj as any).mt5Login} (${fromAccountObj.Name || (fromAccountObj as any).name || 'Account'})`
                            : ''
                        }
                        onChange={value => {
                          const loginNum = value.split(' ')[0];
                          setFromAccount(loginNum);
                          if (errors.fromAccount)
                            setErrors(prev => ({
                              ...prev,
                              fromAccount: undefined,
                            }));
                        }}
                        emptyText="No accounts found."
                      />
                      {errors.fromAccount && (
                        <p className="text-xs text-red-500 font-medium">
                          {errors.fromAccount}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="toAccount">
                        To Account <span className="text-red-500">*</span>
                      </Label>
                      <SourceCombobox
                        id="toAccount"
                        items={toAccountsList.map(
                          acc =>
                            `${acc.Login || (acc as any).mt5Login} (${acc.Name || (acc as any).name || 'Account'})`
                        )}
                        placeholder="Select to account"
                        value={
                          toAccountObj
                            ? `${toAccountObj.Login || (toAccountObj as any).mt5Login} (${toAccountObj.Name || (toAccountObj as any).name || 'Account'})`
                            : ''
                        }
                        onChange={value => {
                          const loginNum = value.split(' ')[0];
                          setToAccount(loginNum);
                          if (errors.toAccount)
                            setErrors(prev => ({
                              ...prev,
                              toAccount: undefined,
                            }));
                        }}
                        emptyText="No accounts found."
                      />
                      {errors.toAccount && (
                        <p className="text-xs text-red-500 font-medium">
                          {errors.toAccount}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="amount">
                        Amount ($) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="amount"
                        type="number"
                        min={MIN_TRANSACTION_AMOUNT}
                        max={MAX_TRANSACTION_AMOUNT}
                        step="0.01"
                        placeholder="Enter transfer amount"
                        value={amount}
                        onKeyDown={preventInvalidNumberInput}
                        onPaste={preventInvalidNumberPaste}
                        onChange={e => {
                          setAmount(e.target.value);
                          if (errors.amount)
                            setErrors(prev => ({ ...prev, amount: undefined }));
                        }}
                      />
                      {errors.amount && (
                        <p className="text-xs text-red-500 font-medium">
                          {errors.amount}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="comment">
                        Comment <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="comment"
                        placeholder="For example: Move funds to MT5 account"
                        minLength={MIN_COMMENT_LENGTH}
                        maxLength={MAX_COMMENT_LENGTH}
                        value={comment}
                        onChange={e => {
                          setComment(e.target.value);
                          if (errors.comment)
                            setErrors(prev => ({
                              ...prev,
                              comment: undefined,
                            }));
                        }}
                      />
                      {errors.comment && (
                        <p className="text-xs text-red-500 font-medium">
                          {errors.comment}
                        </p>
                      )}
                    </div>
                  </div>

                  {(fromAccountObj || toAccountObj) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 rounded-md bg-muted/40 border text-xs">
                      {fromAccountObj && (
                        <div>
                          <span className="text-muted-foreground block font-medium">
                            From Account Details:
                          </span>
                          <span className="font-semibold text-sm block">
                            Login: {fromAccountObj.Login} (
                            {fromAccountObj.Name || 'Account'})
                          </span>
                          <span className="text-muted-foreground block">
                            Available Balance:{' '}
                            <strong className="text-green-600 dark:text-green-400">
                              $
                              {(fromAccountObj.Balance || 0).toLocaleString(
                                'en-US',
                                { minimumFractionDigits: 2 }
                              )}
                            </strong>
                          </span>
                        </div>
                      )}
                      {toAccountObj && (
                        <div>
                          <span className="text-muted-foreground block font-medium">
                            To Account Details:
                          </span>
                          <span className="font-semibold text-sm block">
                            Login: {toAccountObj.Login} (
                            {toAccountObj.Name || 'Account'})
                          </span>
                          <span className="text-muted-foreground block">
                            Available Balance:{' '}
                            <strong className="text-green-600 dark:text-green-400">
                              $
                              {(toAccountObj.Balance || 0).toLocaleString(
                                'en-US',
                                { minimumFractionDigits: 2 }
                              )}
                            </strong>
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-2 rounded-md border border-dashed p-3 text-xs text-muted-foreground bg-muted/30">
                    <p>
                      Internal transfers are processed instantly and do not
                      incur fees.
                    </p>
                    <p>
                      Make sure you keep enough margin in the source account to
                      support any open positions.
                    </p>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setFromAccount('');
                        setToAccount('');
                        setAmount('');
                        setComment('');
                        setErrors({});
                      }}
                      disabled={isSubmitting}
                    >
                      Reset
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(USER_ROUTES.USER_WALLET)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Processing...' : 'Confirm Transfer'}
                    </Button>
                  </div>
                </form>
              </LoaderOverlay>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <ul className="space-y-2 list-disc list-inside">
                <li>
                  Use internal transfers to rebalance risk across your MT4/MT5
                  accounts.
                </li>
                <li>
                  Funds can only be transferred between accounts under the same
                  profile.
                </li>
                <li>
                  Leverage and margin requirements may differ between accounts.
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </LoaderOverlay>
  );
};

export default Transfer;
